import React, { useState, useEffect } from 'react';
import OTPInput from './OTPInput';
import api from '../services/api';

const OTPVerification = ({ 
  email, 
  purpose, 
  onVerified, 
  onCancel, 
  title = 'Verify Your Email',
  subtitle = 'Enter the 6-digit code sent to your email',
  skipVerification = false // For login flow, skip separate verification
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    // Start countdown timer
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleOTPComplete = async (otpValue) => {
    // For login flow, skip separate verification and pass OTP directly to parent
    if (skipVerification) {
      if (onVerified) onVerified(otpValue);
      return;
    }
    
    await verifyOTP(otpValue);
  };

  const verifyOTP = async (otpValue) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/otp/verify', {
        email,
        otp: otpValue,
        purpose
      });

      if (response.data.success) {
        setSuccess('OTP verified successfully!');
        setTimeout(() => {
          if (onVerified) onVerified(otpValue);
        }, 500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/otp/resend', {
        email,
        purpose
      });

      if (response.data.success) {
        setSuccess('New OTP sent to your email!');
        setResendTimer(60);
        setCanResend(false);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lightbox" style={{ zIndex: 9999 }}>
      <div 
        className="card" 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '500px',
          padding: '40px 32px',
          textAlign: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,253,250,0.98))',
          boxShadow: '0 20px 60px rgba(122,19,43,0.2)',
          borderRadius: '16px'
        }}
      >
        {/* Close button */}
        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: 'var(--maroon)',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        )}

        {/* Icon */}
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>📧</div>

        {/* Title */}
        <h2 style={{
          fontFamily: "'Playfair Display',serif",
          color: 'var(--maroon)',
          marginBottom: '12px',
          fontSize: '28px'
        }}>
          {title}
        </h2>

        {/* Subtitle */}
        <p style={{
          color: 'var(--muted)',
          marginBottom: '8px',
          fontSize: '15px'
        }}>
          {subtitle}
        </p>

        {/* Email display */}
        <p style={{
          color: 'var(--maroon)',
          fontWeight: '600',
          marginBottom: '30px',
          fontSize: '16px'
        }}>
          {email}
        </p>

        {/* OTP Input */}
        <OTPInput
          length={6}
          onComplete={handleOTPComplete}
          disabled={loading}
        />

        {/* Error message */}
        {error && (
          <div style={{
            background: 'rgba(220, 53, 69, 0.1)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            color: '#dc3545',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Success message */}
        {success && (
          <div style={{
            background: 'rgba(40, 167, 69, 0.1)',
            border: '1px solid rgba(40, 167, 69, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            color: '#28a745',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '16px' }}>
            Verifying OTP...
          </p>
        )}

        {/* Resend OTP */}
        <div style={{ marginTop: '20px' }}>
          {canResend ? (
            <button
              onClick={handleResendOTP}
              disabled={loading}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--maroon)',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              Resend OTP
            </button>
          ) : (
            <p style={{ color: 'var(--muted)', fontSize: '14px', margin: 0 }}>
              Resend OTP in {resendTimer} seconds
            </p>
          )}
        </div>

        {/* Help text */}
        <p style={{
          color: 'var(--muted)',
          fontSize: '12px',
          marginTop: '20px',
          lineHeight: '1.6'
        }}>
          Didn't receive the code? Check your spam folder or click "Resend OTP" after the timer expires.
        </p>
      </div>
    </div>
  );
};

export default OTPVerification;
